import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { captureHydrationException, getSliceState } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import { ShiftNote, ShiftNoteSerializedEntity, ShiftNotesState } from './types';

export const shiftNotesAdapter = createEntityAdapter<ShiftNote>();

export const initialState: ShiftNotesState = {
  ...shiftNotesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state: ShiftNotesState, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const shiftNotes = (
              action?.payload?.included as ShiftNoteSerializedEntity[]
            )
              .filter(
                (entity: ShiftNoteSerializedEntity) =>
                  entity.type === 'shiftNote'
              )
              .map((entity: ShiftNoteSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                text: entity.attributes.text,
                createdAt: entity.attributes.createdAt,
                author: entity.attributes.author,
                ...Object.fromEntries(
                  Object.entries(entity.relationships || {}).map(
                    ([k, v]: [string, any]) => [k, v.data]
                  )
                ),
              }));
            shiftNotesAdapter.setAll(state, shiftNotes);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );

    builder.addCase('resource/shift/create', (state, action: any) => {
      const shift = action.payload?.data;
      if (shift?.attributes?.note && shift?.relationships?.note?.data?.id) {
        const noteId = shift.relationships.note.data.id;
        const existingNote = state.entities[noteId];
        if (existingNote) {
          shiftNotesAdapter.updateOne(state, {
            id: noteId,
            changes: {
              text: shift.attributes.note,
            },
          });
        }
      }

      if (action.payload?.included) {
        const shiftNotes = action.payload.included
          .filter((entity: any) => entity.type === 'shiftNote')
          .map((entity: any) => ({
            id: entity.id,
            type: entity.type,
            text: entity.attributes.text,
            createdAt: entity.attributes.createdAt,
            author: entity.attributes.author,
          }));

        if (shiftNotes.length > 0) {
          shiftNotesAdapter.upsertMany(state, shiftNotes);
        }
      }
    });

    builder.addCase('resource/shift/update', (state, action: any) => {
      const shift = action.payload?.data;
      if (shift?.attributes?.note && shift?.relationships?.note?.data?.id) {
        const noteId = shift.relationships.note.data.id;
        const existingNote = state.entities[noteId];
        if (existingNote) {
          shiftNotesAdapter.updateOne(state, {
            id: noteId,
            changes: {
              text: shift.attributes.note,
            },
          });
        }
      }

      if (action.payload?.included) {
        const shiftNotes = action.payload.included
          .filter((entity: any) => entity.type === 'shiftNote')
          .map((entity: any) => ({
            id: entity.id,
            type: entity.type,
            text: entity.attributes.text,
            createdAt: entity.attributes.createdAt,
            author: entity.attributes.author,
          }));

        if (shiftNotes.length > 0) {
          shiftNotesAdapter.upsertMany(state, shiftNotes);
        }
      }
    });
  },
});

export const reducer = slice.reducer;

export const {
  selectById: selectShiftNoteById,
  selectIds: selectShiftNoteIds,
  selectEntities: selectShiftNoteEntities,
  selectAll: selectAllShiftNotes,
  selectTotal: selectTotalShiftNotes,
} = shiftNotesAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);
